<script lang="ts">
	import Card from '@smui/card';
	import Checkbox from '@smui/checkbox';
	import Select, { Option } from '@smui/select';
	import Input from '@smui/textfield';
	import { _ } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import FieldLoading from '../../../../..//shared/components/common/Loading/FieldLoading.svelte';
	import TableLoading from '../../../../..//shared/components/common/Loading/TableLoading.svelte';
	import Pagination from '../../../../..//shared/components/common/Table/Pagination.svelte';
	import Title from '../../../../..//shared/components/common/Title.svelte';
	import ExpandIcon from '../../../../../shared/components/common/Icons/ExpandIcon.svelte';
	import { formatDateTime } from '../../../../../shared/functions';
	import Area from '../../../../../shared/models/Area/Area';
	import FiltroGenerico from '../../../../../shared/models/Filtri/FiltroGenerico';
	import Questionario from '../../../../../shared/models/Questionario/Questionario';
	import SetQuestionario from '../../../../../shared/models/Questionario/SetQuestionario';
	import { hasRoleSysAdmin } from '../../../../../shared/stores';

	let filters: FiltroGenerico = {
		descrizione: '',
		daApprovareAziendaEnte: false,
		areaId: null,
		setQuestionarioId: null,
		pagina: 1,
		elementiPerPagina: 10,
	};

	$: [data, loading, error] = Questionario.getListConAziendeRispondenti(filters);

	let [dataAree, loadingAree, errorAree] = Area.getList();
	let [dataSetQuestionari, loadingSetQuestionari, errorSetQuestionari] = SetQuestionario.getList();

	let selectedArea: Area = new Area();
	let selectedSetQuestionario: SetQuestionario = new SetQuestionario();

	function onResetFilters() {
		filters.descrizione = '';
		if ($hasRoleSysAdmin) {
			selectedArea = new Area();
			filters.areaId = null;
		}
		filters.setQuestionarioId = null;
		selectedSetQuestionario = new SetQuestionario();
	}

	let showRow = {};

	function toggleRow(rowId) {
		showRow[rowId] = !showRow[rowId];
	}

	$: disabled = !isAnyAziendaRispondenteSelected && !isAnyQuestionarioSelected;

	$: isAnyQuestionarioSelected = $data?.response?.some((questionario) => questionario?.selected === true);

	$: isAnyAziendaRispondenteSelected = $data?.response?.some(questionario => 
		questionario.aziendeRispondenti.some(azienda => azienda.selected === true)
	);

	function goToReport() {
		const selectedQuestionariIds = $data?.response?.filter(a => a.selected === true).map(a => a.id);
		localStorage.setItem('questionariAziendeReportId', JSON.stringify(selectedQuestionariIds));

		const selectedAziendeRispondentiIds = $data?.response?.reduce((accumulator, questionario) => {
			if (questionario.selected) {
				const selectedIds = questionario.aziendeRispondenti.filter(azienda => azienda.selected === true).map(azienda => azienda.aziendaEnte.id);
				accumulator.push(...selectedIds);
			}
			return accumulator;
		}, []);

		localStorage.setItem('aziendeRispondentiId', JSON.stringify(selectedAziendeRispondentiIds));

		const storedQuestionariAziendeReportId = JSON.parse(localStorage.getItem('questionariAziendeReportId') || '[]');
		const storedAziendeRispondentiId = JSON.parse(localStorage.getItem('aziendeRispondentiId') || '[]');

		if (storedQuestionariAziendeReportId.length > 0 && storedAziendeRispondentiId.length > 0) {
			push(`#/questionariaziendereport`);
		}
	}
</script>

<Card class="md:px-8 md:py-8" padded>
	<div class="flex justify-between">
		<Title
			title="{$_('page.reporting.company/entity.list.title')}"
			icon="help_outline"
			iconText="{$_('page.reporting.company/entity.list.helper_text_basic')}"
		/>
		<div class="sm:flex-nowrap sm:space-x-4 flex flex-wrap">
			<button title="{$_('global.reset_filters')}" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{onResetFilters}">
				<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">restart_alt</span>
			</button>
			<button {disabled} title="{$_('global.report')}" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{goToReport}">
				<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">summarize</span>
			</button>
		</div>
	</div>
	<div class="md:grid-cols-2 lg:grid-cols-3 grid grid-cols-1 gap-8 p-4">
		<Input label="{$_('page.survey.list.description')}" bind:value="{filters.descrizione}" />
		{#if $hasRoleSysAdmin}
			{#if $loadingAree}
				<FieldLoading />
			{:else}
				<Select key="{(area) => `${(area && area.id) || ''}`}" label="{$_('page.survey.list.area')}" bind:value="{selectedArea}">
					{#if $dataAree.response}
						<Option
							value="{{}}"
							on:click="{() => {
								filters.areaId = null;
							}}"
						/>
						{#each $dataAree.response as area (area.id)}
							{#if area.nomeComune}
								<Option value="{area}" on:click="{() => (filters.areaId = area.id)}">{area.nomeComune}</Option>
							{/if}
						{/each}
					{/if}
				</Select>
			{/if}
		{/if}
		{#if $loadingSetQuestionari}
			<FieldLoading />
		{:else}
			<Select key="{(setQuestionario) => `${(setQuestionario && setQuestionario.id) || ''}`}" label="{$_('page.survey.list.set_questionario')}" bind:value="{selectedSetQuestionario}">
				{#if $dataSetQuestionari.response}
					<Option
						value="{{}}"
						on:click="{() => {
							filters.setQuestionarioId = null;
						}}"
					/>
					{#each $dataSetQuestionari.response as setQuestionario (setQuestionario.id)}
						{#if setQuestionario.nome}
							<Option value="{setQuestionario}" on:click="{() => (filters.setQuestionarioId = setQuestionario.id)}">{setQuestionario.nome}</Option>
						{/if}
					{/each}
				{/if}
			</Select>
		{/if}
	</div>
	{#if $loading}
		<TableLoading />
	{:else}
		<div class="flex flex-col">
			<div class="sm:-mx-6 lg:-mx-8 -my-2 overflow-x-auto">
				<div class="sm:px-6 lg:px-8 inline-block min-w-full py-2 align-middle">
					<div class="overflow-hidden">
						<table class="min-w-full p-0 m-0 text-sm leading-10 border-t-4 border-collapse border-black table-auto">
							<thead>
								<tr class="p-1">
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
									></th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.code')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.description')}</th
									>
									{#if $hasRoleSysAdmin}
										<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
											>{$_('page.survey.list.area')}</th
										>
									{/if}
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.start_validity')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.survey.list.end_validity')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">
										Risposte ricevute
									</th>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">
										Percentuale risposte
									</th>
								</tr>
							</thead>
							<tbody>
								{#if $data?.response}
									{#each $data?.response as row}
										<tr
											class="odd:bg-white even:bg-gray-50 hover:bg-gray-50 p-1"
										>
											<td class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
											><ExpandIcon condition={showRow[row.id]} onClick="{() => toggleRow(row?.id)}"/></td>
											<td class="border-b p-2.5 text-center leading-5">{row.codice}</td>
											<td class="border-b p-2.5 text-center leading-5">{row.descrizione}</td>
											{#if $hasRoleSysAdmin}
												<td class="border-b p-2.5 text-center leading-5"
													>{row.areaId && !$loadingAree
														? $dataAree?.response?.find((x) => x.id === row.areaId)?.nomeComune
														: ''}</td
												>
											{/if}
											<td class="border-b p-2.5 text-center leading-5"
												>{formatDateTime(row.dataInizioValidita)}</td
											>
											<td class="border-b p-2.5 text-center leading-5"
												>{formatDateTime(row.dataFineValidita)}</td
											>
											<td class="border-b p-2.5 text-center leading-5">{row.numeroRisposteRicevute} / {row.numeroTotaleLavoratori}</td>
											<td class="border-b p-2.5 text-center leading-5">{row.percentualeRisposte.toFixed(2)}%</td>
											<td class="leading-5 text-center border">
												<Checkbox 
													checked="{row.aziendeRispondenti.some((value) => value.selected === true)}" 
													on:change="{(e) => { 
														row.aziendeRispondenti.forEach((azienda) => (azienda.selected = e.target.checked)); 
														row.selected = e.target.checked;
														row = row }}"
												/>
											</td>
										</tr>
										{#if showRow[row?.id]}
											<tr id="{row.id}">
												<td colspan="8" class="p-4 border">
													{#if row.aziendeRispondenti.length > 0}
														<div class="h-48 overflow-auto">
															<table class="min-w-full p-0 m-0 text-sm leading-10 table-auto">
																<thead>
																	<tr class="p-1">
																		<th scope="col" class="text-xs leading-6 tracking-wider text-center border"
																			>{$_('page.company/entity.list.company/entity_name')}</th
																		>
																		<th scope="col" class="text-xs leading-6 tracking-wider text-center border"
																			>{$_('page.company/entity.list.address')}</th
																		>
																		<th scope="col" class="text-xs leading-6 tracking-wider text-center border">
																			Risposte ricevute</th
																		>
																		<th scope="col" class="text-xs leading-6 tracking-wider text-center border">
																			Percentuale risposte</th
																		>
																		<th scope="col" class="text-xs leading-6 tracking-wider text-center border"
																		>
																			<Checkbox
																				checked="{row.aziendeRispondenti.every((value) => value.selected === true)}"
																				on:change="{(e) => { row.aziendeRispondenti.forEach((azienda) => (azienda.selected = e.target.checked)); row.selected = e.target.checked; row = row }}"
																			/>
																		</th
																		>
																	</tr>
																</thead>
																<tbody>
																	{#each row.aziendeRispondenti as rowAziendaEnte}
																		<tr class="odd:bg-white even:bg-gray-50">
																			<td class="leading-5 text-center border"
																				>{rowAziendaEnte.aziendaEnte.nomeAziendaEnte ?? ''}</td
																			>
																			<td class="leading-5 text-center border"
																				>{rowAziendaEnte.aziendaEnte.indirizzo ?? ''}
																				{#if rowAziendaEnte.aziendaEnte.provincia && rowAziendaEnte.aziendaEnte.citta && rowAziendaEnte.aziendaEnte.cap && rowAziendaEnte.aziendaEnte.indirizzoOK}
																					<span
																						title="Indirizzo valido"
																						class="material-icons-outlined"
																						style="color: green; font-size: 20px;">fmd_good</span
																					>
																				{:else}
																					<span
																						title="Indirizzo non valido"
																						class="material-icons-outlined"
																						style="color: red; font-size: 20px;">wrong_location</span
																					>
																				{/if}</td
																			>
																			<td class="leading-5 text-center border">
																				{rowAziendaEnte.numeroRisposteRicevute} / {rowAziendaEnte.numeroTotaleLavoratori}
																			</td>
																			<td class="leading-5 text-center border">
																				{rowAziendaEnte.percentualeRisposte.toFixed(2)}%
																			</td>
																			<td class="leading-5 text-center border"
																				><Checkbox
																					bind:checked="{rowAziendaEnte.selected}"
																					on:click="{() => rowAziendaEnte.selected = !rowAziendaEnte.selected}"
																					on:change="{() => { 
																						if (row.aziendeRispondenti.some((aziendaRispondente) => aziendaRispondente?.selected === true)) {
																							row.selected = true;
																						} else {
																							row.selected = false;
																						}
																						}}"
																				/></td
																			>
																		</tr>
																	{/each}
																</tbody>
															</table>
														</div>
													{:else}
														<span class="w-full p-4">{$_('page.reporting.company/entity.list.no_responding_company/entity')}</span>
													{/if}
												</td>
											</tr>
										{/if}
									{/each}
								{/if}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
		<div class="right-0">
			<Pagination bind:current="{filters.pagina}" bind:num_pages="{$data.totalPages}" bind:per_page="{filters.elementiPerPagina}" />
		</div>
	{/if}
</Card>
